/** CADRUL STRUCTURII ÎN DESEN.
 *
 *  Modelul FEM stă în cadrul lui: originea lui în desen (`origine_stereo70`,
 *  în metri Stereo 70, în cadrul desenului — +x est, +y SUD, adică −nord) și
 *  rotația θ a grilei dominante; un punct al modelului ajunge în desen prin
 *  `spre_desen_m` (`structure/fem.py`): rotit cu +θ și translat cu originea.
 *  Macheta stă în ACELAȘI cadru al desenului, dar adus la originea locală a
 *  planului de situație (`origineDesen`, kilometrul rotunjit). Deci grupul
 *  structurii se așază în `lume` cu rotația θ și translația (x0 − ox, y0 − oy).
 *
 *  Pe verticală, z = 0 al modelului e pardoseala celui mai de jos nivel al
 *  lui (subsolul, când e), iar ±0,00 al machetei e pardoseala parterului:
 *  grupul coboară cu cota pardoselii parterului din model. */

export interface AsezareaStructurii {
  /** translația în cadrul local al machetei, m */
  x: number;
  y: number;
  /** cât coboară modelul ca pardoseala parterului să cadă la ±0,00 */
  z: number;
  /** rotația grupului în jurul lui z, rad */
  theta: number;
}

type ModelCuCadru = {
  origine_stereo70?: number[] | null;
  theta_rad?: number | null;
  etaje?: { nivel?: string | null; z_m: number; inaltime_m?: number | null; index?: number }[] | null;
};

/** Cota pardoselii PARTERULUI în cadrul modelului: capul etajului numit
 *  «parter» minus înălțimea lui; fără un asemenea etaj, primul etaj care
 *  nu e subsol; fără niciunul, 0. */
export function pardoseala_parter(etaje: ModelCuCadru["etaje"]): number {
  const list = etaje ?? [];
  const eSubsol = (n: string | null | undefined) => /subsol|demisol|basement|parcaj/i.test(n ?? "");
  const parter = list.find((e) => /parter|ground/i.test(e.nivel ?? ""))
    ?? list.find((e) => !eSubsol(e.nivel));
  if (!parter) return 0;
  return parter.z_m - (parter.inaltime_m ?? 3);
}

/** Unde stă grupul structurii în macheta planului de situație cu originea
 *  locală `origine` (cea a documentului de situație, `origineDesen`).
 *  `null` când modelul nu-și știe originea în desen (un model vechi). */
export function asezareaStructurii(model: ModelCuCadru, origine: [number, number]): AsezareaStructurii | null {
  const o = model.origine_stereo70;
  if (!o || o.length < 2 || !Number.isFinite(o[0]) || !Number.isFinite(o[1])) return null;
  // ROTAȚIA e a treia valoare a originii (`fem.py: cadrul`): modelul unui corp
  // singur are `theta_rad` = 0 (unghiul GRILEI lui, nu al așezării în desen),
  // dar originea lui poartă θ-ul desenului; complexul le are pe amândouă egale
  const theta = o.length > 2 && Number.isFinite(o[2]) ? Number(o[2]) : (Number.isFinite(model.theta_rad) ? Number(model.theta_rad) : 0);
  return { x: o[0] - origine[0], y: o[1] - origine[1], z: -pardoseala_parter(model.etaje), theta };
}

/** Un punct al modelului adus în cadrul local al machetei (pentru probe și
 *  teste: exact ce face three cu `position` + `rotation.z` ale grupului). */
export function inMacheta(a: AsezareaStructurii, x: number, y: number, z = 0): [number, number, number] {
  const c = Math.cos(a.theta), s = Math.sin(a.theta);
  return [c * x - s * y + a.x, s * x + c * y + a.y, z + a.z];
}
